import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { Link } from "react-router-dom";
import { message, Popover } from "antd";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronUp, RotateCcw } from "feather-icons-react";
import { setToogleHeader } from "../../../core/redux/action";
import { fetchProductVariationStatus } from "../../../core/api/products";
import { secureExportToExcel } from "../../../utils/secureExcelUtils";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";

const ProductVariationStatus = React.forwardRef(({ addDynamicTab }, ref) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const selectedClients = useSelector((state) => state.selectedClients);

  const [variations, setVariations] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5000);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [currentTableData, setCurrentTableData] = useState([]);

  const gridApiRef = useRef(null);
  const pendingRequestRef = useRef(null);

  // Expose fetchVariations method to parent component
  React.useImperativeHandle(ref, () => ({
    fetchVariations: (page, pageSize) => fetchVariations(page || currentPage, pageSize || 5000),
  }));

  const fetchVariations = async (page = 1, pageSize = 1000) => {
    // Build unique request key
    const requestKey = `${selectedClients.join(",")}-${page}-${pageSize}-${searchQuery}`;

    // Skip if identical request already in-flight
    if (pendingRequestRef.current === requestKey) {
      console.log("Skipping duplicate request", requestKey);
      return;
    }

    // Mark this request as pending
    pendingRequestRef.current = requestKey;

    // Ensure page and pageSize are valid numbers
    const validPage = typeof page === "number" && !isNaN(page) ? page : 1;
    const validPageSize = typeof pageSize === "number" && !isNaN(pageSize) ? pageSize : 5000;

    setLoading(true);

    // Show loading overlay in the grid if it's available
    if (gridApiRef.current) {
      gridApiRef.current.showLoadingOverlay();
    }

    // Log the start time to measure performance
    const startTime = performance.now();

    try {
      const response = await fetchProductVariationStatus(
        selectedClients,
        validPage,
        validPageSize,
        searchQuery
      );

      console.log("variation status response: ", response);

      // Log the API response time
      const apiResponseTime = performance.now() - startTime;
      console.log(`Variation Status API response time: ${apiResponseTime.toFixed(2)}ms`);

      // Update state with new data
      setVariations(response.data.results);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.page);
      setPageSize(response.data.page_size);
      setTotalPages(response.data.total_pages);
      setDataLoaded(true);

      // Update the grid with new data if it exists
      if (gridApiRef.current) {
        const api = gridApiRef.current;

        // Make sure we're on the right page
        if (api.paginationProxy) {
          const currentPage = api.paginationProxy.getCurrentPage() + 1;
          if (currentPage !== validPage) {
            api.paginationProxy.setPage(validPage - 1);
          }
        }

        // Update the page size if needed
        if (api.paginationProxy && api.paginationProxy.getPageSize() !== validPageSize) {
          api.paginationProxy.setPageSize(validPageSize);
        }
      }
    } catch (err) {
      console.error("Error fetching product variations status:", err);

      // Show no rows overlay if there's an error
      if (gridApiRef.current) {
        gridApiRef.current.showNoRowsOverlay();
      }
      message.error("Failed to load product variations status. Please try again.");
    } finally {
      // Log total operation time including state updates
      const totalTime = performance.now() - startTime;
      console.log(`Total variation status data loading time: ${totalTime.toFixed(2)}ms`);

      // Clear pending request flag
      if (pendingRequestRef.current === requestKey) {
        pendingRequestRef.current = null;
      }
      setLoading(false);

      // Hide loading overlay
      if (gridApiRef.current) {
        gridApiRef.current.hideOverlay();
      }
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  useEffect(() => {
    // Only fetch data when selectedClients changes and prevent duplicate initial loads
    if (selectedClients && selectedClients.length > 0 && !dataLoaded) {
      fetchVariations(1, pageSize);
    } else if (!selectedClients || selectedClients.length === 0) {
      // Clear data if no clients selected
      setVariations([]);
      setTotalRows(0);
      setDataLoaded(false);
    }
  }, [selectedClients]);

  // Handle search with debounce
  useEffect(() => {
    if (dataLoaded) {
      const debounceTimer = setTimeout(() => {
        fetchVariations(1, pageSize);
      }, 500);

      return () => clearTimeout(debounceTimer);
    }
  }, [searchQuery]);

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const exportToExcel = async () => {
    const exportColumns = [
      { title: "Brand", dataIndex: "brand_name" },
      { title: "Product", dataIndex: "product_name" },
      { title: "Product Code", dataIndex: "product_code" },
      { title: "Product Type", dataIndex: "product_type" },
      { title: "Product Universe", dataIndex: "product_universe" },
      { title: "Variation Name", dataIndex: "variation_name" },
      { title: "Variation Code", dataIndex: "variation_code" },
      { title: "Variation Code Display", dataIndex: "variation_code_display" },
      { title: "Status", dataIndex: "status_name" },
      { title: "Formula Lab Status", dataIndex: "formula_lab_status" },
      { title: "Labels Lab Status", dataIndex: "labels_lab_status" },
      { title: "Supplement Facts", dataIndex: "supplement_facts" },
    ];

    const exportData = currentTableData.map((variation) => {
      const row = {};
      exportColumns.forEach((col) => {
        row[col.title] = variation[col.dataIndex] || "";
      });
      return row;
    });

    secureExportToExcel(exportData, "product_variation_status.xlsx");
  };

  // Status badge renderer
  const renderStatusBadge = useCallback((status) => {
    if (!status) return <span className="badge bg-secondary">Not Set</span>;

    switch (status.toLowerCase()) {
      case "approved":
        return <span className="badge bg-success">✅ Approved</span>;
      case "pending":
        return <span className="badge bg-warning">🕒 Pending</span>;
      case "requested":
        return <span className="badge bg-info">📝 Requested</span>;
      default:
        return <span className="badge bg-secondary">{status}</span>;
    }
  }, []);

  // Supplement facts renderer with popover for long text
  const renderSupplementFacts = useCallback((params) => {
    const text = params.value || "";
    const maxLength = 50;

    if (!text) {
      return <span className="text-muted">No supplement facts</span>;
    }

    if (text.length <= maxLength) {
      return <span>{text}</span>;
    }

    const truncatedText = text.substring(0, maxLength) + "...";

    const popoverContent = (
      <div
        style={{ maxWidth: "400px", maxHeight: "300px", overflow: "auto", whiteSpace: "pre-wrap" }}>
        {text}
      </div>
    );

    return (
      <Popover
        content={popoverContent}
        title="Supplement Facts"
        trigger="click"
        placement="topLeft">
        <span
          style={{ cursor: "pointer", color: "#1890ff", textDecoration: "underline" }}
          title="Click to view full text">
          {truncatedText}
        </span>
      </Popover>
    );
  }, []);

  const columnDefs = [
    {
      headerName: "#",
      valueGetter: (params) => {
        return (currentPage - 1) * pageSize + params.node.rowIndex + 1;
      },
      width: 70,
      minWidth: 70,
      maxWidth: 80,
      sortable: false,
      filter: false,
    },
    {
      headerName: "Brand",
      field: "brand_name",
      width: 140,
      minWidth: 120,
    },
    {
      headerName: "Product",
      field: "product_name",
      width: 200,
      minWidth: 180,
      flex: 1.5,
      cellRenderer: (params) => {
        const record = params.data;
        return (
          <span
            className="clickable-link"
            onClick={() => {
              if (addDynamicTab) {
                addDynamicTab("view-product", {
                  title: `View Product (${record.id})`,
                  clientId: record.client_id,
                  productId: record.id,
                  variationId: record.variation_id,
                });
              }
            }}>
            <u>{params.value}</u>
          </span>
        );
      },
    },
    {
      headerName: "Product Code",
      field: "product_code",
      width: 130,
      minWidth: 110,
    },
    {
      headerName: "Product Type",
      field: "product_type",
      width: 130,
      minWidth: 110,
    },
    {
      headerName: "Variation Name",
      field: "variation_name",
      width: 160,
      minWidth: 140,
      flex: 1,
    },
    {
      headerName: "Variation Code",
      field: "variation_code",
      width: 140,
      minWidth: 120,
    },
    {
      headerName: "Variation Display Code",
      field: "variation_code_display",
      width: 160,
      minWidth: 140,
      cellRenderer: (params) => {
        const record = params.data;
        return (
          <span
            className="clickable-link"
            onClick={() => {
              if (addDynamicTab) {
                addDynamicTab("view-product", {
                  title: `View Product (${record.id})`,
                  clientId: record.client_id,
                  productId: record.id,
                  variationId: record.variation_id,
                });
              }
            }}>
            <u>{params.value}</u>
          </span>
        );
      },
    },
    {
      headerName: "Status",
      field: "status_name",
      width: 120,
      minWidth: 100,
      cellRenderer: (params) => {
        const value = params.value || "";
        const badgeClass =
          value === "Confirmed"
            ? "badge bg-success"
            : value === "Pending"
            ? "badge bg-warning"
            : "badge bg-danger";
        return <span className={badgeClass}>{value}</span>;
      },
    },
    {
      headerName: "Formula Lab Status",
      field: "formula_lab_status",
      width: 160,
      minWidth: 140,
      cellRenderer: (params) => renderStatusBadge(params.value),
    },
    {
      headerName: "Labels Lab Status",
      field: "labels_lab_status",
      width: 160,
      minWidth: 140,
      cellRenderer: (params) => renderStatusBadge(params.value),
    },
    {
      headerName: "Supplement Facts",
      field: "supplement_facts",
      width: 200,
      minWidth: 180,
      flex: 1,
      cellRenderer: (params) => renderSupplementFacts(params),
    },
  ];

  return (
    <div>
      <div className="p-1">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Product Variation Status</h4>
            </div>
          </div>

          <ul className="table-top-head">
            <div className="search-set">
              <div className="search-input">
                <input
                  type="text"
                  placeholder="Search variations..."
                  className="form-control form-control-sm formsearch"
                  value={searchQuery}
                  onChange={handleSearch}
                />
                <Link to="#" className="btn btn-searchset">
                  <i data-feather="search" className="feather-search" />
                </Link>
              </div>
            </div>
            <li>
              <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                <Link data-bs-toggle="tooltip" data-bs-placement="top" onClick={exportToExcel}>
                  <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
                  {/* <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" /> */}
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                <Link
                  to="#"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  onClick={(e) => {
                    e.preventDefault();
                    fetchVariations(currentPage, pageSize);
                  }}>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch(setToogleHeader(!data));
                  }}>
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>

        <div className="table-responsive">
          <div
            className="ag-theme-quartz position-relative"
            style={{ height: "700px", width: "100%" }}>
            {/* Loading Overlay */}
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 1000,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ marginTop: "15px", fontWeight: "bold" }}>
                  Loading variation status...
                </div>
              </div>
            )}

            <AgGridReact
              columnDefs={columnDefs}
              rowData={variations}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
                minWidth: 100,
                wrapText: true,
              }}
              pagination={true}
              paginationPageSize={pageSize}
              paginationPageSizeSelector={[10, 25, 50, 100, 500, 1000, 2000, 5000]}
              domLayout="normal"
              rowModelType="clientSide"
              suppressPaginationPanel={true}
              paginationAutoPageSize={false}
              onFirstDataRendered={(params) => {
                params.api.sizeColumnsToFit();
                if (params.api.paginationProxy) {
                  params.api.setPagination(true);
                  params.api.paginationProxy.setPageSize(pageSize);
                }
              }}
              onGridReady={(params) => {
                gridApiRef.current = params.api;

                // If we're currently loading data, show the loading overlay
                if (loading) {
                  params.api.showLoadingOverlay();
                }

                // Only fetch data on grid ready if we haven't loaded it yet
                if (!dataLoaded && selectedClients && selectedClients.length > 0) {
                  fetchVariations(currentPage, pageSize);
                } else {
                  setTimeout(() => {
                    params.api.paginationGoToPage(currentPage - 1);
                  }, 100);
                }
              }}
              onPaginationChanged={(params) => {
                if (!params.api || loading) return;

                if (params.api.paginationProxy) {
                  const newPage = params.api.paginationProxy.getCurrentPage() + 1;
                  const newPageSize = params.api.paginationProxy.getPageSize();

                  if (newPage !== currentPage || newPageSize !== pageSize) {
                    setCurrentPage(newPage);
                    setPageSize(newPageSize);
                    fetchVariations(newPage, newPageSize);
                  }
                }
              }}
              onRowDataUpdated={(params) => {
                if (params.api) {
                  const allData = [];
                  params.api.forEachNode((node) => {
                    allData.push(node.data);
                  });
                  setCurrentTableData(allData);
                }
              }}
            />

            <div
              className="custom-pagination-footer"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px 20px",
                borderTop: "1px solid #ddd",
                position: "sticky",
                bottom: 0,
                backgroundColor: "white",
                zIndex: 1000,
                boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
              }}>
              <div className="pagination-info">
                Showing {variations.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
                {Math.min(currentPage * pageSize, totalRows)} of {totalRows} entries
              </div>
              <div
                className="pagination-controls"
                style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <div className="page-size-selector">
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = parseInt(e.target.value);
                      setPageSize(newSize);
                      fetchVariations(1, newSize);
                    }}
                    style={{ padding: "5px", border: "1px solid #ddd", borderRadius: "4px" }}>
                    {[10, 25, 50, 100, 500, 1000, 2000, 5000].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="page-navigation" style={{ display: "flex", gap: "5px" }}>
                  <button
                    onClick={() => fetchVariations(1, pageSize)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      opacity: currentPage === 1 ? 0.5 : 1,
                    }}>
                    First
                  </button>
                  <button
                    onClick={() => fetchVariations(currentPage - 1, pageSize)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      opacity: currentPage === 1 ? 0.5 : 1,
                    }}>
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => fetchVariations(pageNum, pageSize)}
                        style={{
                          padding: "5px 10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: currentPage === pageNum ? "#007bff" : "white",
                          color: currentPage === pageNum ? "white" : "black",
                        }}>
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => fetchVariations(currentPage + 1, pageSize)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                      opacity: currentPage === totalPages ? 0.5 : 1,
                    }}>
                    Next
                  </button>
                  <button
                    onClick={() => fetchVariations(totalPages, pageSize)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                      opacity: currentPage === totalPages ? 0.5 : 1,
                    }}>
                    Last
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ProductVariationStatus.displayName = "ProductVariationStatus";

export default ProductVariationStatus;
